import { useEffect, useState } from 'react';
import { api } from '../api';
import type { DomainSummary } from '../types';

const TIER_DOMAIN_LIMITS: Record<string, number> = {
  free:   0,
  tier_1: 1,
  tier_2: 5,
  tier_3: 10,
};

interface SwapBudgetData {
  swapsRemaining: number;
  nextResetAt: string;
}

interface Props {
  tier: string;
  onAddDomain: () => void;
  onSelectDomain: (id: string) => void;
  onStartOnboarding?: (domainId: string) => void;
}

interface DeletedDomainEntry {
  id: string;
  domain: string;
  deletedAt: string;
}

interface SwapLimitError {
  error: 'swap_limit_reached';
  message: string;
  next_reset_at: string;
  current_tier: string;
  swaps_used: number;
  swaps_limit: number;
  upgrade_url: string | null;
}

const STATE_LABELS: Record<string, string> = {
  pending: 'Pending verification',
  scanning: 'Scanning',
  optimised: 'Optimised',
};

const STATE_COLORS: Record<string, string> = {
  pending: 'text-status-pending',
  scanning: 'text-status-scanning',
  optimised: 'text-status-optimised',
};

const PLANS: { tier: 'tier_1' | 'tier_2' | 'tier_3'; name: string; price: string; domains: string; highlight?: boolean; features: string[] }[] = [
  {
    tier: 'tier_1',
    name: 'Starter',
    price: '$79',
    domains: '1 domain',
    features: [
      'Full technical SEO + AEO audit',
      'Schema graph generation',
      'Auto-fix injection (AFI)',
      'GEO citation monitoring',
      'AI-ready FAQ pairs',
    ],
  },
  {
    tier: 'tier_2',
    name: 'Pro',
    price: '$179',
    domains: 'Up to 5 domains',
    highlight: true,
    features: [
      'Everything in Starter',
      'Content gap + keyword analysis',
      'Outreach Pipeline',
      'Brand voice extraction',
      '90-day audit cadence + history',
      'Citation authority recs',
    ],
  },
  {
    tier: 'tier_3',
    name: 'Agency',
    price: '$349',
    domains: 'Up to 10 domains',
    features: [
      'Everything in Pro',
      'Agency overview dashboard',
      'Client domain labelling',
      'Fortnightly re-audit cadence',
      'Competitor AEO benchmarking',
      'Monthly GEO strategy brief',
    ],
  },
] as const;

function UpgradeBanner() {
  const [loading, setLoading] = useState<string | null>(null);

  async function handleUpgrade(tier: 'tier_1' | 'tier_2' | 'tier_3') {
    setLoading(tier);
    try {
      const { url } = await api.billing.checkout(tier);
      window.location.href = url;
    } catch {
      setLoading(null);
    }
  }

  return (
    <div className="py-10">
      <p className="text-2xl font-semibold text-brand-ink mb-2 text-center">Choose a plan to get started</p>
      <p className="text-gray-500 text-sm mb-10 text-center">Upgrade takes 30 seconds. Cancel any time.</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {PLANS.map((plan) => (
          <div
            key={plan.tier}
            className={`bg-white rounded-2xl p-7 border shadow-sm flex flex-col ${
              plan.highlight ? 'border-brand-green ring-2 ring-brand-green' : 'border-gray-100'
            }`}
          >
            {plan.highlight && (
              <p className="text-xs font-semibold text-brand-green uppercase tracking-widest mb-3">Most popular</p>
            )}
            <p className="text-xs uppercase tracking-widest text-gray-400 mb-1">{plan.name}</p>
            <p className="text-3xl font-bold text-brand-ink mb-0.5">
              {plan.price}<span className="text-base font-normal text-gray-400">/mo</span>
            </p>
            <p className="text-xs text-gray-400 mb-5">{plan.domains}</p>
            <ul className="text-sm text-gray-600 space-y-2 mb-8 flex-1">
              {plan.features.map((f) => (
                <li key={f}>✓ {f}</li>
              ))}
            </ul>
            <button
              onClick={() => handleUpgrade(plan.tier)}
              disabled={loading !== null}
              className={`w-full font-medium rounded-lg py-2.5 text-sm transition-colors disabled:opacity-50 ${
                plan.highlight
                  ? 'bg-brand-green text-white hover:bg-brand-green-dark'
                  : 'bg-gray-100 text-brand-ink hover:bg-gray-200'
              }`}
            >
              {loading === plan.tier ? 'Redirecting…' : `Get ${plan.name}`}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export function DomainList({ tier, onAddDomain, onSelectDomain }: Props) {
  const [domains, setDomains] = useState<DomainSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [needsUpgrade, setNeedsUpgrade] = useState(false);

  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [savingId, setSavingId] = useState<string | null>(null);
  const [editError, setEditError] = useState('');

  // Delete state
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Swap limit error modal
  const [swapLimitError, setSwapLimitError] = useState<SwapLimitError | null>(null);

  // Swap budget (for inline note)
  const [swapData, setSwapData] = useState<SwapBudgetData | null>(null);

  // Recently deleted domains
  const [deletedDomains, setDeletedDomains] = useState<DeletedDomainEntry[]>([]);
  const [recoveringId, setRecoveringId] = useState<string | null>(null);

  useEffect(() => {
    api.domains
      .list()
      .then(setDomains)
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : 'Failed to load domains';
        if (msg.toLowerCase().includes('upgrade')) {
          setNeedsUpgrade(true);
        } else {
          setError(msg);
        }
      })
      .finally(() => setLoading(false));

    if (tier !== 'free') {
      api.domains.listDeleted().then(setDeletedDomains).catch(() => {/* non-critical */});
      api.domains.getSwapBudget().then(setSwapData).catch(() => {/* non-critical */});
    }
  }, [tier]);

  function startEdit(e: React.MouseEvent, id: string, currentDomain: string) {
    e.stopPropagation();
    setEditingId(id);
    setEditValue(currentDomain);
    setEditError('');
    setConfirmDeleteId(null);
  }

  function cancelEdit(e: React.MouseEvent) {
    e.stopPropagation();
    setEditingId(null);
    setEditValue('');
    setEditError('');
  }

  async function handleSaveEdit(e: React.MouseEvent, id: string) {
    e.stopPropagation();
    const trimmed = editValue.trim();
    if (!trimmed) return;
    setSavingId(id);
    setEditError('');
    try {
      const res = await api.domains.update(id, trimmed);
      setDomains((prev) => prev.map((d) => d.id === id ? { ...d, domain: res.domain, verified: false, state: 'pending' } : d));
      setEditingId(null);
    } catch (err: unknown) {
      setEditError(err instanceof Error ? err.message : 'Failed to update domain');
    } finally {
      setSavingId(null);
    }
  }

  function startConfirmDelete(e: React.MouseEvent, id: string) {
    e.stopPropagation();
    setConfirmDeleteId(id);
    setEditingId(null);
  }

  function cancelDelete(e: React.MouseEvent) {
    e.stopPropagation();
    setConfirmDeleteId(null);
  }

  async function handleDelete(e: React.MouseEvent, id: string) {
    e.stopPropagation();
    setDeletingId(id);
    try {
      await api.domains.delete(id);
      const deleted = domains.find((d) => d.id === id);
      setDomains((prev) => prev.filter((d) => d.id !== id));
      setConfirmDeleteId(null);
      // Add to recently-deleted list
      if (deleted) {
        setDeletedDomains((prev) => [
          { id: deleted.id, domain: deleted.domain, deletedAt: new Date().toISOString() },
          ...prev,
        ]);
      }
    } catch {
      setConfirmDeleteId(null);
    } finally {
      setDeletingId(null);
    }
  }

  async function handleRecover(id: string) {
    setRecoveringId(id);
    try {
      const recovered = await api.domains.recover(id);
      setDeletedDomains((prev) => prev.filter((d) => d.id !== id));
      // Add back to active domains list
      setDomains((prev) => [
        {
          id: recovered.id,
          domain: recovered.domain,
          verified: recovered.verified,
          state: recovered.state,
          type: recovered.type as 'owned' | 'tracked',
        } as DomainSummary,
        ...prev,
      ]);
    } catch {
      // Silent — user sees nothing changed
    } finally {
      setRecoveringId(null);
    }
  }

  const tierLimit = TIER_DOMAIN_LIMITS[tier] ?? 0;
  const atDomainLimit = tier !== 'free' && domains.length >= tierLimit;

  return (
    <div className="bg-brand-paper">
      <div className="max-w-3xl mx-auto px-4 pt-10 pb-20">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-2xl font-semibold text-brand-ink">Your domains</h1>
          {tier !== 'free' && !atDomainLimit && (
            <button
              onClick={onAddDomain}
              className="bg-brand-green text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-brand-green-dark transition-colors"
            >
              + Add domain
            </button>
          )}
          {atDomainLimit && (
            <button
              onClick={async () => {
                const nextTier = tier === 'tier_1' ? 'tier_2' : tier === 'tier_2' ? 'tier_3' : null;
                if (nextTier) {
                  const { url } = await api.billing.checkout(nextTier);
                  window.location.href = url;
                }
              }}
              className="text-xs text-gray-400 hover:text-brand-green transition-colors"
            >
              Upgrade to add more →
            </button>
          )}
        </div>

        {loading && <p className="text-gray-400 text-sm">Loading…</p>}
        {error && <p className="text-red-500 text-sm">{error}</p>}
        {needsUpgrade && <UpgradeBanner />}
        {!loading && !error && !needsUpgrade && domains.length === 0 && (
          <div className="text-center py-20">
            {tier === 'free' ? (
              <>
                <p className="text-gray-400 text-base mb-1">No domains yet.</p>
                <p className="text-sm text-gray-400 mb-4">Upgrade to Starter to add your first domain.</p>
              </>
            ) : (
              <>
                <p className="text-gray-400 text-base mb-4">No domains yet.</p>
                <button
                  onClick={onAddDomain}
                  className="bg-brand-green text-white text-sm font-medium px-6 py-2.5 rounded-lg hover:bg-brand-green-dark transition-colors"
                >
                  Add your first domain
                </button>
              </>
            )}
          </div>
        )}

        <div className="space-y-3">
          {domains.map((d, idx) => {
            const isLastDomain = idx === domains.length - 1;
            const isEditing = editingId === d.id;
            const isConfirmingDelete = confirmDeleteId === d.id;
            const isDeleting = deletingId === d.id;
            const isSaving = savingId === d.id;
            // Edit is only available before the snippet is verified live on the site
            const canEdit = !d.verified;

            return (
              <div
                key={d.id}
                className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden"
              >
                {/* Main row */}
                <div className="flex items-stretch">
                  {/* Clickable domain info — disabled while editing or confirming delete */}
                  <button
                    onClick={() => !isEditing && !isConfirmingDelete && onSelectDomain(d.id)}
                    disabled={isEditing || isConfirmingDelete || isDeleting}
                    className="flex-1 text-left px-5 py-4 hover:bg-gray-50 transition-colors disabled:cursor-default disabled:hover:bg-white"
                  >
                    <p className="font-medium text-brand-ink">{d.domain}</p>
                    <p className={`text-xs mt-0.5 ${STATE_COLORS[d.state] ?? 'text-gray-400'}`}>
                      {STATE_LABELS[d.state] ?? d.state}
                      {!d.verified && ' · Unverified'}
                    </p>
                  </button>

                  {/* Action icons — hidden while editing or confirming */}
                  {!isEditing && !isConfirmingDelete && (
                    <div className="flex items-center gap-1 pr-3">
                      {canEdit && (
                        <button
                          onClick={(e) => startEdit(e, d.id, d.domain)}
                          title="Edit domain URL"
                          className="p-2 text-gray-300 hover:text-brand-ink transition-colors rounded-lg hover:bg-gray-50"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                          </svg>
                        </button>
                      )}
                      <button
                        onClick={(e) => startConfirmDelete(e, d.id)}
                        title="Delete domain"
                        className="p-2 text-gray-300 hover:text-red-500 transition-colors rounded-lg hover:bg-red-50"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </button>
                      <span className="text-gray-200 text-lg pl-1">›</span>
                    </div>
                  )}
                </div>

                {/* Inline edit form */}
                {isEditing && (
                  <div className="border-t border-gray-100 px-5 py-3 bg-gray-50">
                    <p className="text-xs text-gray-500 mb-2">Edit domain URL — this will reset verification and state to pending.</p>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Escape') { setEditingId(null); setEditError(''); } }}
                        className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:border-brand-green"
                        autoFocus
                        spellCheck={false}
                      />
                      <button
                        onClick={(e) => handleSaveEdit(e, d.id)}
                        disabled={isSaving || !editValue.trim() || editValue.trim() === d.domain}
                        className="text-sm font-medium bg-brand-green text-white px-3 py-1.5 rounded-lg hover:bg-brand-green-dark disabled:opacity-40 transition-colors"
                      >
                        {isSaving ? 'Saving…' : 'Save'}
                      </button>
                      <button
                        onClick={cancelEdit}
                        className="text-sm text-gray-500 hover:text-gray-700 px-2 py-1.5"
                      >
                        Cancel
                      </button>
                    </div>
                    {editError && <p className="text-xs text-red-500 mt-1.5">{editError}</p>}
                  </div>
                )}

                {/* Inline delete confirmation */}
                {isConfirmingDelete && (
                  <div className="border-t border-red-100 px-5 py-3 bg-red-50">
                    <p className="text-xs text-red-700 mb-2">
                      Delete <span className="font-medium">{d.domain}</span>? The domain will be soft-deleted and you can recover it within 30 days.
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => handleDelete(e, d.id)}
                        disabled={isDeleting}
                        className="text-sm font-medium bg-red-600 text-white px-3 py-1.5 rounded-lg hover:bg-red-700 disabled:opacity-40 transition-colors"
                      >
                        {isDeleting ? 'Deleting…' : 'Yes, delete'}
                      </button>
                      <button
                        onClick={cancelDelete}
                        className="text-sm text-gray-600 hover:text-gray-800 px-2 py-1.5"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Swap note — bottom-right of last card */}
                {isLastDomain && swapData && (
                  <div className="flex justify-end px-5 pb-2">
                    <p className="text-[11px] text-gray-300">
                      {swapData.swapsRemaining} swap{swapData.swapsRemaining === 1 ? '' : 's'} remaining
                      {' · '}Resets {new Date(swapData.nextResetAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Recently deleted domains */}
        {deletedDomains.length > 0 && (
          <div className="mt-10">
            <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3">Recently deleted</h2>
            <div className="space-y-2">
              {deletedDomains.map((d) => {
                const expiresAt = new Date(d.deletedAt);
                expiresAt.setDate(expiresAt.getDate() + 30);
                const daysLeft = Math.max(0, Math.ceil((expiresAt.getTime() - Date.now()) / 86400000));
                return (
                  <div
                    key={d.id}
                    className="bg-white border border-gray-100 rounded-xl px-5 py-3 flex items-center justify-between shadow-sm"
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-500">{d.domain}</p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {daysLeft > 0 ? `${daysLeft} day${daysLeft === 1 ? '' : 's'} to recover` : 'Recovery window expired'}
                      </p>
                    </div>
                    {daysLeft > 0 && (
                      <button
                        onClick={() => handleRecover(d.id)}
                        disabled={recoveringId === d.id}
                        className="text-xs font-medium text-brand-green hover:text-brand-green-dark disabled:opacity-40 transition-colors px-3 py-1.5 border border-brand-green rounded-lg"
                      >
                        {recoveringId === d.id ? 'Recovering…' : 'Recover'}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Swap limit modal */}
      {swapLimitError && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl p-7 max-w-sm w-full shadow-xl">
            <p className="text-lg font-semibold text-brand-ink mb-2">Domain swap limit reached</p>
            <p className="text-sm text-gray-600 mb-4">{swapLimitError.message}</p>
            <p className="text-xs text-gray-400 mb-5">
              Resets on {new Date(swapLimitError.next_reset_at).toLocaleDateString(undefined, { month: 'long', day: 'numeric' })}.
            </p>
            <div className="flex gap-3">
              {swapLimitError.upgrade_url && (
                <a
                  href={swapLimitError.upgrade_url}
                  className="flex-1 text-center text-sm font-medium bg-brand-green text-white px-4 py-2.5 rounded-lg hover:bg-brand-green-dark transition-colors"
                >
                  Upgrade plan
                </a>
              )}
              <button
                onClick={() => setSwapLimitError(null)}
                className="flex-1 text-sm text-gray-600 hover:text-gray-800 px-4 py-2.5 border border-gray-200 rounded-lg"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
